"use client";

import Image from "next/image";
import Link from "next/link";
import { ArrowLeft, ArrowUpRight, Download, Share2 } from "lucide-react";
import { useTranslations } from "next-intl";
import { Noticia } from "@/lib/api/datosGenerales/types/noticias";

interface NoticiaShellProps {
  noticia: Noticia;
  locale: "es" | "en";
  tiempoLectura: number;
  noticiasRelacionadas: Noticia[];
  /** Prefijo de las rutas de noticias según el lado (comercial "/noticias" o "/corporativo/noticias") */
  basePath?: string;
  /** Metadatos extra que se muestran en el hero (chips de video, imágenes, etc.) */
  extraMeta?: React.ReactNode;
  /** Bloque visual entre el action bar y el contenido (video, carrusel) */
  topMedia?: React.ReactNode;
}

export default function NoticiaShell({
  noticia,
  locale,
  tiempoLectura,
  noticiasRelacionadas,
  basePath = "/corporativo/noticias",
  extraMeta,
  topMedia,
}: NoticiaShellProps) {
  const t = useTranslations("noticiasPage");

  const handleShare = async () => {
    if (typeof navigator !== "undefined" && navigator.share) {
      try {
        await navigator.share({
          title: noticia.titulo,
          url: window.location.href,
        });
      } catch (err) {
        console.error("Share canceled:", err);
      }
    } else if (typeof navigator !== "undefined") {
      await navigator.clipboard.writeText(window.location.href);
      alert(t("detail.linkCopied"));
    }
  };

  return (
    <main className="min-h-screen bg-white">
      {/* Hero */}
      <section className="relative h-[55vh] min-h-[420px] flex items-end overflow-hidden">
        <Image
          src={noticia.cover_image}
          alt={noticia.titulo}
          fill
          sizes="100vw"
          className="object-cover blur-xs"
          priority
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/40 to-black/10" />
        <div className="relative z-10 max-w-[80%] mx-auto w-full pb-16 md:pb-20">
          <div className="max-w-4xl">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-white font-semibold mb-4">
              <span className="w-8 h-px bg-white" />
              {t("titleCard")}
            </span>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-white tracking-tight leading-tight mb-6 text-balance">
              {noticia.titulo}
            </h1>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-xs uppercase tracking-wider text-white/80 font-mono">
              <span>{noticia.fecha}</span>
              <span>{t("detail.readingTime", { min: tiempoLectura })}</span>
              {extraMeta}
            </div>
          </div>
        </div>
      </section>

      {/* Action bar */}
      <section className="border-b border-gray-200">
        <div className="max-w-[80%] mx-auto py-5 flex items-center justify-between gap-4">
          <Link
            href={`/${locale}${basePath}`}
            className="inline-flex items-center gap-2 text-xs uppercase tracking-wider font-semibold text-[#102542] hover:gap-3 transition-all"
          >
            <ArrowLeft className="w-4 h-4" />
            {t("detail.back")}
          </Link>
          <div className="flex items-center gap-3">
            {noticia.kit_prensa_archivo && (
              <a
                href={noticia.kit_prensa_archivo}
                target="_blank"
                rel="noopener noreferrer"
                download
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-gray-200 text-xs uppercase tracking-wider font-semibold text-gray-700 hover:border-[#102542] hover:text-[#102542] transition-colors"
              >
                <Download className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">{t("detail.mediaKit")}</span>
              </a>
            )}
            <button
              onClick={handleShare}
              className="w-9 h-9 rounded-full border border-gray-200 flex items-center justify-center text-gray-700 hover:border-[#102542] hover:text-[#102542] transition-colors"
              aria-label={t("detail.share")}
            >
              <Share2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>

      {/* Medio embebido opcional */}
      {topMedia && (
        <section className="max-w-4xl mx-auto px-4 pt-12">{topMedia}</section>
      )}

      {/* Contenido del artículo */}
      <article className="max-w-[80%] md:max-w-[67%] mx-auto py-12 md:py-16">
        <div
          className="prose prose-lg max-w-4xl mx-auto
            prose-headings:text-gray-900 prose-headings:tracking-tight
            prose-p:text-gray-700 prose-p:leading-relaxed
            prose-a:text-[#102542] prose-a:no-underline hover:prose-a:underline
            prose-strong:text-gray-900
            prose-blockquote:border-l-[#102542] prose-blockquote:bg-gray-50 prose-blockquote:py-1 prose-blockquote:px-4 prose-blockquote:rounded-r-lg
            prose-img:rounded-2xl"
          dangerouslySetInnerHTML={{ __html: noticia.contenido }}
        />
      </article>

      {/* Footer */}
      <section className="border-t border-gray-200">
        <div className="max-w-[80%] mx-auto py-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <p className="text-sm text-gray-500">
            {t("detail.publishedOn")}{" "}
            <span className="font-medium text-gray-900">{noticia.fecha}</span>
          </p>
          <Link
            href={`/${locale}${basePath}`}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#102542] text-white rounded-full text-xs uppercase tracking-wider font-semibold hover:bg-[#0a1a30] transition-colors"
          >
            {t("detail.viewMore")}
            <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* Noticias relacionadas */}
      {noticiasRelacionadas.length > 0 && (
        <section className="max-w-[80%] mx-auto py-20">
          <div className="max-w-3xl mb-12">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#102542] font-semibold mb-4">
              <span className="w-8 h-px bg-[#102542]" />
              {t("detail.relatedEyebrow")}
            </span>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 tracking-tight">
              {t("detail.relatedTitle")}
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-gray-200 rounded-2xl overflow-hidden border border-gray-200">
            {noticiasRelacionadas.map((rel) => (
              <Link
                key={rel.id}
                href={`/${locale}${basePath}/${rel.slug}`}
                className="group bg-white flex flex-col transition-colors hover:bg-gray-50"
              >
                <div className="relative aspect-[16/10] overflow-hidden bg-gray-100">
                  <Image
                    src={rel.cover_image}
                    alt={rel.titulo}
                    fill
                    sizes="(max-width: 640px) 80vw, (max-width: 1024px) 40vw, 27vw"
                    className="object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <span className="text-xs text-gray-500 mb-3">
                    {rel.fecha}
                  </span>
                  <h3 className="text-base font-semibold text-gray-900 leading-snug line-clamp-3 mb-6">
                    {rel.titulo}
                  </h3>
                  <span className="mt-auto inline-flex items-center gap-1 text-[#102542]">
                    <ArrowUpRight className="w-4 h-4" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </main>
  );
}
